// ./src/components/features/Venues/VenueEditDrawer/VenueEditDrawer.tsx
//
// Create/edit drawer for a Venue (Library entity). Self-loading in edit mode via
// recordId; context-or-props resolution; no success toast (the page highlight is
// the confirmation). Fields: name, categories, contact details, capacity, notes,
// status. Addresses split into a required (place venues) non-removable "Primary
// address" section + a repeatable "Additional addresses" list.

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Stack, TextInput, Textarea, NumberInput, Group, Divider, Text, Skeleton } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { Venue, VenueInput, venueLogoUrl } from "@/utils/api";
import { getFormErrors, getIndexedFormErrors, formatErrors } from "@/utils";
import { useCreateGate, useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { FEATURE_CODES } from "@/config";
import { PlaceData } from "@/hooks/useGoogleMapsAutocomplete";
import { useOptionalVenuesContext } from "@/contexts/VenuesContext";
import { useOptionalVenueCategoriesContext } from "@/contexts/VenueCategoriesContext";
import { AppDrawer, StatusInput, PlaceAutocompleteInput, MediaImageField, MediaImageRef } from "@/components/generic";
import { VenueCategorySelect } from "../VenueCategorySelect/VenueCategorySelect";
import {
  VenueAddressesEditor,
  VenueAddressFields,
  VenueAddressForm,
  createEmptyVenueAddress,
  splitVenueAddresses,
  isVenueAddressEmpty,
  applyAddressDataToForm,
  venueAddressToInput,
  venueAddressesToInput,
} from "../VenueAddressesEditor/VenueAddressesEditor";
import { ServicePointsEditor, ServicePointRow, servicePointRowsFrom, servicePointRowsToInput } from "../../ServicePoints/ServicePointsEditor";
import {
  VenueRequirementsEditor,
  VenueRequirementRow,
  venueRequirementRowsFrom,
  venueRequirementRowsToInput,
} from "../VenueRequirementsEditor/VenueRequirementsEditor";

// Google place types treated as a real, lookup-able venue (not a private home).
const VENUE_PLACE_TYPES = ["establishment"];

const PLACE_GROUP = "place";
const VENDOR_KIND = "vendor";

const recordIsPlace = (record: Venue): boolean =>
  (record.categories ?? []).some((c) => c.categoryGroup === PLACE_GROUP);

// Pull one address into the primary/office slot for a place-hosting venue or any
// vendor (its office). A plain service-less venue keeps all addresses additional.
const recordExtractsPrimary = (record: Venue): boolean =>
  record.kind === VENDOR_KIND || recordIsPlace(record);

export interface VenueEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** 'venue' (default) or 'vendor' — controls category groups + address requiredness. */
  kind?: string;
  onCreated?: (venue: Venue) => void;
  onUpdated?: (venue: Venue) => void;
}

interface FormData {
  name: string;
  categoryIds: string[];
  description: string;
  logo: MediaImageRef | null;
  website: string;
  email: string;
  phone: string;
  contactName: string;
  capacity: number | "";
  notes: string;
  status: number;
  primaryAddress: VenueAddressForm;
  additionalAddresses: VenueAddressForm[];
  /** #169 — entrances, desks, merch tables (a place that hosts events; not a vendor). */
  servicePoints: ServicePointRow[];
  /** #170 — what the venue requires of the vendors working there (a place; not a vendor; vendor compliance plan). */
  vendorRequirements: VenueRequirementRow[];
}

// ⚠ Only list a name here if something in this file READS `fieldErrors.<name>`. `getFormErrors` puts a KNOWN
// name in `fields` and NEVER in `genericErrors`, so a listed field with no control SWALLOWS its message
// entirely and the save fails with nothing on screen — strictly worse than not listing it, because an
// UNLISTED name still reaches the footer prefixed with itself.
const KNOWN_FORM_FIELDS = [
  "name",
  "logoUploadedFileId",
  "categoryIds",
  "description",
  "website",
  "email",
  "phone",
  "contactName",
  "capacity",
  "notes",
  "status",
  "addresses",
];

const getInitialFormData = (): FormData => ({
  name: "",
  categoryIds: [],
  description: "",
  logo: null,
  website: "",
  email: "",
  phone: "",
  contactName: "",
  capacity: "",
  notes: "",
  status: STATUSES.ACTIVE,
  primaryAddress: createEmptyVenueAddress(),
  additionalAddresses: [],
  servicePoints: [],
  vendorRequirements: [],
});

const fromRecord = (record: Venue): FormData => {
  const { primary, additional } = splitVenueAddresses(record, recordExtractsPrimary(record));

  return {
    name: record.name || "",
    categoryIds: (record.categories ?? []).map((c) => c.id).filter((id): id is string => !!id),
    description: record.description || "",
    logo: record.logoUploadedFileId
      ? { uploadedFileId: record.logoUploadedFileId, url: venueLogoUrl(record) }
      : null,
    website: record.website || "",
    email: record.email || "",
    phone: record.phone || "",
    contactName: record.contactName || "",
    capacity: typeof record.capacity === "number" ? record.capacity : "",
    notes: record.notes || "",
    status: record.status ?? STATUSES.ACTIVE,
    primaryAddress: primary,
    additionalAddresses: additional,
    servicePoints: servicePointRowsFrom(record.servicePoints),
    vendorRequirements: venueRequirementRowsFrom(record.vendorRequirements),
  };
};

const serialize = (data: FormData): string => JSON.stringify(data);

const VenueEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={90} radius="sm" />
    <Skeleton height={60} radius="sm" />
  </Stack>
);

export const VenueEditDrawer: React.FC<VenueEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  kind = "venue",
  onCreated,
  onUpdated,
}) => {
  const { t } = useTranslation(["venues", "common"]);

  const isVendor = kind === "vendor";
  // #170 — "Vendors must provide" is vendor compliance (`production`): a place's, on a plan that has it.
  const compliancePlan = useCreateGate(FEATURE_CODES.PRODUCTION);
  // Changed (and sent) only on a plan that has it; on a downgraded one a venue's list stays READABLE (owner, 2026-10-20).
  const editRequirements = !isVendor && compliancePlan.canCreate;
  // Vendors may only pick service categories; venues get both groups.
  const allowedCategoryGroups = isVendor ? ["service"] : undefined;

  const ctx = useOptionalVenuesContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<Venue | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as Venue);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || operationError;

  // #169 — a point can name one of the venue's SAVED addresses (a new one has no id until the venue is saved).
  const savedAddressOptions = useMemo(
    () =>
      [formData.primaryAddress, ...formData.additionalAddresses]
        .filter((a) => !!a.id)
        .map((a) => ({
          value: a.id as string,
          label: a.label.trim() || [a.streetAddress, a.city].map((x) => x.trim()).filter(Boolean).join(", ") || "Address",
        })),
    [formData.primaryAddress, formData.additionalAddresses]
  );

  const { fields: fieldErrors, genericErrors: allGenericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );
  // #169 — a point's refusal is keyed `servicePoints[<i>].<field>` and shown at that row, so it stays out of the footer.
  const { byIndex: pointErrors } = useMemo(() => getIndexedFormErrors(effectiveError, "servicePoints"), [effectiveError]);
  // #170 — likewise a requirement's, keyed `vendorRequirements[<i>].<field>`. (A refusal of the whole list keeps its bare
  // name and reaches the footer.)
  const { byIndex: requirementErrors } = useMemo(() => getIndexedFormErrors(effectiveError, "vendorRequirements"), [effectiveError]);
  const genericErrors = useMemo(
    () => allGenericErrors.filter((m) => !/^servicePoints\[\d+\]\./.test(m)).filter((m) => !/^vendorRequirements\[\d+\]\./.test(m)),
    [allGenericErrors]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record ? fromRecord(record) : getInitialFormData();
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    clearErrRef.current?.();
  }, [opened, record, isEditMode]);

  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Category group lookup (place vs service) — a "place" category makes the
  // primary address a required, non-removable section.
  const categoriesCtx = useOptionalVenueCategoriesContext();
  const categoryGroupById = useMemo(() => {
    const map = new Map<string, string>();
    (categoriesCtx?.categories ?? []).forEach((c) => {
      if (c.id) {
        map.set(c.id, c.categoryGroup || "");
      }
    });

    return map;
  }, [categoriesCtx?.categories]);

  // #170 — the kinds of vendor a requirement can be limited to (service categories).
  const serviceCategoryOptions = useMemo(
    () =>
      (categoriesCtx?.categories ?? [])
        .filter((c) => c.id && c.categoryGroup === "service")
        .map((c) => ({ value: c.id as string, label: c.name ?? "" })),
    [categoriesCtx?.categories]
  );

  // A vendor never requires a primary address; a venue requires one once it has a
  // place category (it hosts events somewhere).
  const requiresPrimary = useMemo(
    () => !isVendor && formData.categoryIds.some((id) => categoryGroupById.get(id) === PLACE_GROUP),
    [isVendor, formData.categoryIds, categoryGroupById]
  );

  // Vendors always show the (optional) office-address section; venues show it when
  // required or when a primary address already has content.
  const showPrimary = isVendor || requiresPrimary || !isVenueAddressEmpty(formData.primaryAddress);

  // Local (client-side) requiredness error for the primary address.
  const [primaryError, setPrimaryError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setPrimaryError(null);
    }
  }, [opened]);

  const setPrimaryAddress = useCallback((next: VenueAddressForm) => {
    setPrimaryError(null);
    setFormData((prev) => ({ ...prev, primaryAddress: next }));
  }, []);

  // The Name field doubles as a Google place lookup: picking a real establishment
  // autofills website, phone, and the primary address. Typing a custom name (e.g.
  // "My House") leaves everything else untouched (placeData is null).
  const handleNameChange = useCallback((name: string, placeData: PlaceData | null) => {
    setFormData((prev) => {
      const next: FormData = { ...prev, name };

      if (placeData) {
        if (placeData.website) {
          next.website = placeData.website;
        }
        if (placeData.phone) {
          next.phone = placeData.phone;
        }
        next.primaryAddress = applyAddressDataToForm(prev.primaryAddress, placeData.address);
        setPrimaryError(null);
      }

      return next;
    });
  }, []);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);

    const primaryEmpty = isVenueAddressEmpty(formData.primaryAddress);

    // A place venue must have a primary address filled in.
    if (requiresPrimary && primaryEmpty) {
      setPrimaryError(
        t("venues:form.addresses.primaryRequired", {
          defaultValue: "Enter the venue's primary address.",
        })
      );

      return;
    }

    const submitData: VenueInput = {
      kind,
      name: formData.name.trim() || null,
      categoryIds: formData.categoryIds,
      description: formData.description.trim() || null,
      logoUploadedFileId: formData.logo?.uploadedFileId ?? null,
      website: formData.website.trim() || null,
      email: formData.email.trim() || null,
      phone: formData.phone.trim() || null,
      contactName: formData.contactName.trim() || null,
      capacity: isVendor ? null : typeof formData.capacity === "number" ? formData.capacity : null,
      notes: formData.notes.trim() || null,
      status: formData.status,
      // Non-empty primary is sent so the server marks it isPrimary; null clears it.
      primaryAddress: primaryEmpty ? null : venueAddressToInput(formData.primaryAddress),
      addresses: venueAddressesToInput(formData.additionalAddresses),
      // #169 — the whole list, in order; a vendor comes to events and has none.
      ...(isVendor ? {} : { servicePoints: servicePointRowsToInput(formData.servicePoints, true) }),
      // #170 — only where the section is shown (a place, on a plan with vendor compliance); otherwise left untouched.
      ...(editRequirements ? { vendorRequirements: venueRequirementRowsToInput(formData.vendorRequirements) } : {}),
    };

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as Venue[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, kind, requiresPrimary, isEditMode, recordId, createFn, updateFn, onClose, onCreated, onUpdated, t, isVendor, editRequirements]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  const drawerTitle = isEditMode
    ? t("venues:form.editTitle", { defaultValue: "Edit venue" })
    : t("venues:form.addTitle", { defaultValue: "Add venue" });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="lg"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean) || Object.keys(pointErrors).length > 0}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <VenueEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <PlaceAutocompleteInput
            label={t("venues:form.fields.name.label", { defaultValue: "Name" })}
            placeholder={t("venues:form.fields.name.placeholder", {
              defaultValue: "Search a business, or type a name (e.g. My House)",
            })}
            value={formData.name}
            onChange={handleNameChange}
            placeTypes={VENUE_PLACE_TYPES}
            error={fieldErrors.name}
            showDistance={false}
            withinPortal
            size="md"
          />

          <VenueCategorySelect
            label={t("venues:form.fields.categories.label", { defaultValue: "Categories" })}
            description={t("venues:form.fields.categories.description", {
              defaultValue: "How this venue is classified (place and/or service).",
            })}
            value={formData.categoryIds}
            onChange={(ids) => setField("categoryIds", ids)}
            error={fieldErrors.categoryIds}
            allowedGroups={allowedCategoryGroups}
            size="md"
          />

          <Textarea
            label={t("venues:form.fields.description.label", { defaultValue: "Description" })}
            placeholder={t("venues:form.fields.description.placeholder", {
              defaultValue: "A short description of this venue.",
            })}
            value={formData.description}
            onChange={(e) => setField("description", e.currentTarget.value)}
            error={fieldErrors.description}
            autosize
            minRows={2}
            maxRows={5}
            size="md"
          />

          <MediaImageField
            intent="logo"
            allowUrl={false}
            value={formData.logo}
            onChange={(logo) => setField("logo", logo)}
            label={t("venues:form.fields.logo.label", { defaultValue: "Logo" })}
            description={t("venues:form.fields.logo.description", {
              defaultValue: "Shown on your event website and wherever this partner appears.",
            })}
            addLabel={t("venues:form.fields.logo.add", { defaultValue: "Add logo" })}
            error={fieldErrors.logoUploadedFileId}
            disabled={isSubmitting}
          />

          <Group grow align="flex-start">
            <TextInput
              label={t("venues:form.fields.email.label", { defaultValue: "Email" })}
              placeholder="venue@example.com"
              value={formData.email}
              onChange={(e) => setField("email", e.currentTarget.value)}
              error={fieldErrors.email}
              size="md"
            />
            <TextInput
              label={t("venues:form.fields.phone.label", { defaultValue: "Phone" })}
              placeholder="+1 555 123 4567"
              value={formData.phone}
              onChange={(e) => setField("phone", e.currentTarget.value)}
              error={fieldErrors.phone}
              size="md"
            />
          </Group>

          <Group grow align="flex-start">
            <TextInput
              label={t("venues:form.fields.website.label", { defaultValue: "Website" })}
              placeholder="https://example.com"
              value={formData.website}
              onChange={(e) => setField("website", e.currentTarget.value)}
              error={fieldErrors.website}
              size="md"
            />
            <TextInput
              label={t("venues:form.fields.contactName.label", { defaultValue: "Contact person" })}
              placeholder={t("venues:form.fields.contactName.placeholder", { defaultValue: "e.g. Jane Doe" })}
              value={formData.contactName}
              onChange={(e) => setField("contactName", e.currentTarget.value)}
              error={fieldErrors.contactName}
              size="md"
            />
          </Group>

          {/* Capacity is a place concept — hidden for vendors (service providers). */}
          {!isVendor && (
            <NumberInput
              label={t("venues:form.fields.capacity.label", { defaultValue: "Capacity" })}
              placeholder={t("venues:form.fields.capacity.placeholder", { defaultValue: "Max guests" })}
              value={formData.capacity}
              onChange={(v) => setField("capacity", typeof v === "number" ? v : "")}
              error={fieldErrors.capacity}
              min={0}
              allowNegative={false}
              thousandSeparator=","
              size="md"
            />
          )}

          <Textarea
            label={t("venues:form.fields.notes.label", { defaultValue: "Notes" })}
            placeholder={t("venues:form.fields.notes.placeholder", {
              defaultValue: "Private notes (only visible to your team).",
            })}
            value={formData.notes}
            onChange={(e) => setField("notes", e.currentTarget.value)}
            error={fieldErrors.notes}
            autosize
            minRows={2}
            maxRows={5}
            size="md"
          />

          <StatusInput
            value={formData.status}
            onChange={(status) => setField("status", status)}
            error={fieldErrors.status}
          />

          {fieldErrors.addresses && (
            <Text size="md" c="red">
              {fieldErrors.addresses}
            </Text>
          )}

          {showPrimary && (
            <>
              <Divider
                my="xs"
                label={
                  <Group gap={4} wrap="nowrap">
                    <Text size="md" fw={600}>
                      {isVendor
                        ? t("venues:form.addresses.officeLabel", { defaultValue: "Office address" })
                        : t("venues:form.addresses.primaryLabel", { defaultValue: "Primary address" })}
                    </Text>
                    {requiresPrimary && (
                      <Text size="md" c="red" span>
                        *
                      </Text>
                    )}
                  </Group>
                }
                labelPosition="left"
              />

              {requiresPrimary && (
                <Text size="xs" c="dimmed">
                  {t("venues:form.addresses.primaryHint", {
                    defaultValue: "This place needs an address — it can't be removed.",
                  })}
                </Text>
              )}

              {isVendor && (
                <Text size="xs" c="dimmed">
                  {t("venues:form.addresses.officeHint", {
                    defaultValue: "Where this provider is based (optional).",
                  })}
                </Text>
              )}

              {primaryError && (
                <Text size="md" c="red">
                  {primaryError}
                </Text>
              )}

              <VenueAddressFields
                value={formData.primaryAddress}
                onChange={setPrimaryAddress}
                disabled={isSubmitting}
                showLabel={false}
              />
            </>
          )}

          <Divider
            my="xs"
            label={t("venues:form.addresses.additionalLabel", { defaultValue: "Additional addresses" })}
            labelPosition="left"
          />

          <VenueAddressesEditor
            value={formData.additionalAddresses}
            onChange={(addresses) => setField("additionalAddresses", addresses)}
            disabled={isSubmitting}
          />

          {!isVendor && (
            <>
              <Divider
                my="xs"
                label={t("venues:form.points.label", { defaultValue: "Entrances & service points" })}
                labelPosition="left"
              />
              <Text size="md" c="dimmed">
                {t("venues:form.points.hint", {
                  defaultValue:
                    "Gates, desks and tables where your crew scans. On the check-in and redemption pages crew choose where they are, and every check-in and hand-out records it.",
                })}
              </Text>
              <ServicePointsEditor
                value={formData.servicePoints}
                onChange={(rows) => setField("servicePoints", rows)}
                errors={pointErrors}
                addresses={savedAddressOptions}
                disabled={isSubmitting}
              />
            </>
          )}

          {!isVendor && (editRequirements || formData.vendorRequirements.length > 0) && (
            <>
              <Divider
                my="xs"
                label={t("venues:form.vendorRequirements.label", { defaultValue: "Vendors must provide" })}
                labelPosition="left"
              />
              <Text size="md" c="dimmed">
                {!editRequirements
                  ? t("venues:form.vendorRequirements.readOnly", {
                      defaultValue: "Read-only: changing what a venue requires of vendors needs a plan with vendor compliance.",
                    })
                  : t("venues:form.vendorRequirements.hint", {
                      defaultValue:
                        "When a vendor is added to an event here, these are asked of it at once — with the due date counted back from the event. On a vendor's page, \"Add the venue's requirements\" asks for any added later.",
                    })}
              </Text>
              <VenueRequirementsEditor
                value={formData.vendorRequirements}
                onChange={(rows) => setField("vendorRequirements", rows)}
                errors={requirementErrors}
                categoryOptions={serviceCategoryOptions}
                disabled={isSubmitting || !editRequirements}
              />
            </>
          )}
        </Stack>
      )}
    </AppDrawer>
  );
};
